import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react";
import { loggedInUser } from "core/redux/slice/authSlice";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useForm, FormProvider } from "react-hook-form";
import moment from "moment";
import { useGetAllShiftPoliciesQuery } from "AppQuries/shift/policy/shift-policy";
import { getCurrentFormattedDate } from "core/Operations/currentDateTime";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  useCreateTimeLogAttendanceMutation,
  useGetTimeLogDetailsQuery,
  useTimeLogWeelyOffMutation,
} from "AppQuries/attendance/time-log";
import LoadingSpinner from "Components/UI/LoadingSpinner";
import { Book, Download, PlusCircle, Users } from "react-feather";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import TimeLogModal from "Components/Modal/time-log";
import Summary from "./timelog/Summary";
import BulkChange from "./timelog/BulkChange";
import {
  useCreateTimeLogBulkMutation,
  useUpdateTimeLogBulkMutation,
} from "AppQuries/attendance/time-log-bulk";
import { yupResolver } from "@hookform/resolvers/yup";
import { timelogBulkValidationSchema } from "core/Operations/ValidationSchema/timelogBulk";
import { useReactToPrint } from "react-to-print";
import TimelogSheet from "./timelog/TimelogSheet";
import { toast } from "core/Operations/toast";
import TimelogFilterSection from "./timelog/TimelogFilterSection";
import TimelogTable from "./timelog/TimelogTable";
import { useFormattedProjectOption } from "core/Operations/formattedOption/useFormattedMasterOptions";
import { all_routes } from "Router/all_routes";
import {
  useAdjustWaitingTimeMutation,
  useAdjustWTROTAWaitingTimeMutation,
} from "AppQuries/attendance/waiting-time-adjustment";
import { decrypt, safeDecrypt } from "core/Operations/cryptHelper";
import Can from "core/Operations/Can";
import { accessCodes } from "core/json/accessCode";
import WeeklyOff from "./timelog/WeeklyOff";

const TimeLog = () => {
  const userData = useSelector(loggedInUser);
  const [employeeName, setEmployeeName] = useState("");
  const [selectedRow, setSelectedRow] = useState([]);
  const [payload, setPayload] = useState();
  const [show, setShow] = useState(false);
  const [isMount, setIsMount] = useState(false);
  const componentRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClose = () => {
    setShow(false);
    setShowTimeSheet(false);
  };
  const handleShow = () => setShow(true);
  const [showTimeSheet, setShowTimeSheet] = useState(false);

  // Helper to safely decrypt only when param exists

  const [filterData, setFilterData] = useState({
    fromDate:
      safeDecrypt(searchParams, "fromDate") || format(new Date(), "yyyy-MM-dd"),
    toDate:
      safeDecrypt(searchParams, "toDate") || format(new Date(), "yyyy-MM-dd"),
    employeeCode: Number(safeDecrypt(searchParams, "employeeCode")) || 0,
    departmentCode: 0,
  });

  const { mutate: addWeeklyOff, isPending: addingWeeklyOff } =
    useTimeLogWeelyOffMutation();

  const projectsOptions = useFormattedProjectOption({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
  });

  const { data: shifts } = useGetAllShiftPoliciesQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
    isTrue: true,
  });

  const shiftOptions = Array.isArray(shifts)
    ? shifts.map((r) => ({
      ...r,
        label: `${r.shiftName} - (${r.locationCode})`,
        value: r.shiftPolicyID.toString(),
        startTime: r.shiftStartTime,
        endTime: r.shiftEndTime,
      }))
    : [];
  const { mutate: addAttendance, isPending: timelogUpdating } =
    useCreateTimeLogAttendanceMutation();

  const { mutate: addBulkAttendance } = useCreateTimeLogBulkMutation();

  const { mutate: updateBulkAttendance } = useUpdateTimeLogBulkMutation();

  const { mutate: adjustWaitingTime } = useAdjustWaitingTimeMutation();
  const { mutate: adjustWTROTWaitingTime } =
    useAdjustWTROTAWaitingTimeMutation();

  const {
    data: fetchedTimeLogData,
    isLoading,
    isFetching,
    refetch: refetchTimeLog,
  } = useGetTimeLogDetailsQuery({
    companyCode: userData?.companyCode || "",
    locationCode: userData?.selectedLocationCode || "",
    fromDate: filterData?.fromDate || format(new Date(), "yyyy-MM-dd"),
    toDate: filterData?.toDate || format(new Date(), "yyyy-MM-dd"),
    employeeCode: filterData?.employeeCode || 0,
  });

  const methods = useForm({
    defaultValues: {
      userName: userData?.userName,
      attendance: [],
      companyCode: userData?.companyCode,
      locationCode: userData?.locationCode,
      manualEntryData: {
        selectedEmployees: [],
        employeeCode: 0,
        fromDate: format(new Date(), "yyyy-MM-dd"),
        toDate: format(new Date(), "yyyy-MM-dd"),
        haveShift: false,
        totalWorkingHours: 0.0,
      },
    },
  });

  const bulkEntryMethods = useForm({
    defaultValues: {
      userName: userData?.userName,
      companyCode: userData?.companyCode,
      locationCode: userData?.locationCode,
      manualEntryData: {
        selectedEmployees: [],
        employeeCode: 0,
        fromDate: format(new Date(), "yyyy-MM-dd"),
        toDate: format(new Date(), "yyyy-MM-dd"),
        haveShift: false,
        totalWorkingHours: 0.0,
      },
    },
    resolver: yupResolver(timelogBulkValidationSchema),
    mode: "onSubmit",
  });

  const { trigger: triggerBulkChange } = bulkEntryMethods;

  const {
    register,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = methods;

  const resetBulkEnteryData = () => {
    bulkEntryMethods.reset({
      manualEntryData: {
        selectedEmployees: [],
        employeeCode: "",
        fromDate: format(new Date(), "yyyy-MM-dd"),
        toDate: format(new Date(), "yyyy-MM-dd"),
        shiftId: "",
        inTime: "",
        outTime: "",
        projectId: "",
        haveShift: false,
        totalWorkingHours: 0.0,
      },
    });
  };

  const [resetData, setResetData] = useState(false);
  const timeLogBulkChanges = (payload) => {
    updateBulkAttendance(payload, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          toast.success("Time log updated.");
          refetchTimeLog();
          setResetData(true);
        } else {
          toast.warning(data[0].errorMessage);
        }
      },

      onError: (error) => {
        toast.warning(error.message || "Something went wrong.");
      },
    });
  };

  const addBulkAttendanceHandler = (payload) => {
    addBulkAttendance(payload, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          toast.success("Time log Updated Successfully!");
          resetBulkEnteryData();
          handleClose();
          refetchTimeLog();
        } else {
          toast.error(data[0].errorMessage);
        }
      },
      onError: (error) => {
        toast.error(error.message || "Something went wrong.");
      },
    });
  };

  const [count, setCount] = useState(0);

  const addAttendanceHandler = ({ payload, id }) => {
    setCount(count + 1);
    const haveSplit = payload?.attendance?.[0]?.haveSplit;

    addAttendance(payload, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          toast.success("Time log updated.");
          if (haveSplit) {
            refetchTimeLog();
            return;
          }
          if (id !== "") {
            const current = watch("attendance") || [];
            const newAttendance = [...current];
            newAttendance[id] =
              { ...data?.[0]?.attendanceDetails?.[0], id: id } || {};
            setValue("attendance", newAttendance);
            // fetchedTimeLogData.timeLogSummary = data?.[0]?.timeLogSummary;
            // fetchedTimeLogData.waitingSummary = data?.[0]?.waitingSummary;
            // fetchedTimeLogData.workingSummary = data?.[0]?.workingSummary;
            // fetchedTimeLogData.leaveDetails = data?.[0]?.leaveDetails;
            // fetchedTimeLogData.attendanceDetails = data?.[0]?.attendanceDetails;
            // fetchedTimeLogData.employeeProjectWorkingHours =
            //   data?.[0]?.employeeProjectWorkingHours;

            fetchedTimeLogData.attendanceDetails =
              fetchedTimeLogData.attendanceDetails?.map((item, index) =>
                index === id
                  ? {
                      ...item,
                      ...(data?.[0]?.attendanceDetails?.[0] || {}),
                    }
                  : item,
              );
          } else {
            refetchTimeLog();
          }

          // navigate(0);
          // setPayload(null);
        } else {
          toast.warning(data[0].errorMessage);
          // setPayload(null);
        }
      },
      onError: (error) => {
        toast.error(error.message || "Something went wrong.");
      },
    });
  };

  const markWeeklyOffHandler = useCallback(
    async (payload, id) => {
      const requestData = {
        companyCode: userData?.companyCode,
        attendanceDate: payload?.attendanceDate,
        locationCode: userData?.locationCode,
        fromDate: filterData?.fromDate,
        toDate: filterData?.toDate,
        employeeCode: payload?.employeeCode,
        attendance: [
          {
            ...payload,
            createUser: payload?.createUser || userData?.userName,
            createDate: payload?.createDate || getCurrentFormattedDate(),
            modifyUser: userData?.userName,
            modifyDate: getCurrentFormattedDate(),
          },
        ],
      };
      addWeeklyOff(requestData, {
        onSuccess: (data) => {
          if (data?.[0]?.response === true) {
            toast.success("Weekly Off Updated!");

            const current = watch("attendance") || [];
            const newAttendance = [...current];
            newAttendance[id] =
              { ...data?.[0]?.attendanceDetails?.[0], id: id } || {};
            setValue("attendance", newAttendance);
            // fetchedTimeLogData.timeLogSummary = data?.[0]?.timeLogSummary;
            // fetchedTimeLogData.waitingSummary = data?.[0]?.waitingSummary;
            // fetchedTimeLogData.workingSummary = data?.[0]?.workingSummary;
            // fetchedTimeLogData.leaveDetails = data?.[0]?.leaveDetails;
            // fetchedTimeLogData.attendanceDetails = data?.[0]?.attendanceDetails;
            // fetchedTimeLogData.employeeProjectWorkingHours =
            //   data?.[0]?.employeeProjectWorkingHours;
            fetchedTimeLogData.attendanceDetails =
              fetchedTimeLogData.attendanceDetails?.map((item, index) =>
                index === id
                  ? {
                      ...item,
                      ...(data?.[0]?.attendanceDetails?.[0] || {}),
                    }
                  : item,
              );
          } else {
            toast.warning(data?.[0]?.errorMessage);
            // setPayload(null);
          }
        },
        onError: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    },
    [addWeeklyOff, setValue, watch, fetchedTimeLogData],
  );

  const getAttendanceValue = (inTime, outTime) => {
    if (inTime === "00:00:00" && outTime === "00:00:00") return 0;
    const diff = outTime - inTime;
    const hoursDiff = Math.floor(diff / (1000 * 60 * 60));
    const minutesDiff = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    let totalWorkingHours = `${hoursDiff}:${minutesDiff}:00`;
    return totalWorkingHours === "0:0:00" || totalWorkingHours === "0:0"
      ? 0
      : 1;
  };

  const updatingIdsRef = useRef(new Set());

  const handleUpdateAttendance = useCallback(
    (id) => {
      // Prevent duplicate updates for the same id while a previous update is in-flight.
      if (typeof id === "number" && updatingIdsRef.current.has(id)) {
        return;
      }

      const updatedRow = watch("attendance").find((row) => row.id === id);

      

      if (!updatedRow?.inTime && !updatedRow?.outTime) return;

      if (updatedRow?.haveSplit) {
        const splitItemData = watch("attendance")?.filter(
          (row) =>
            row?.attendanceDate === updatedRow?.attendanceDate &&
            row?.employeeCode === updatedRow?.employeeCode,
        );

        // if (item?.isHalfDay) {
        //   const diff = item?.outTime - item?.inTime;
        //   const hoursDiff = Math.floor(diff / (1000 * 60 * 60));
        //   const minutesDiff = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        //   let totalWorkingHours = `${hoursDiff}:${minutesDiff}:00`;
        //   if (totalWorkingHours === "0:0:00" || totalWorkingHours === "0:0") {
        //     toast.error("Please Enter In Time and Out Time");
        //     return;
        //   }
        // }

        const payload = splitItemData?.map((item) => ({
          ...item,
          haveShift: item?.shiftName !== "" ? true : false,
          normalHours: item?.normalWorkingHours || 0,
          attendanceValue: item?.isHalfDay
            ? 0.5
            : item?.attendanceValue === 0
              ? 0
              : updatedRow?.isPaidLeave && !updatedRow?.isFullPaidLeave
                ? 0.5
                : getAttendanceValue(
                    item?.inTime || "00:00:00",
                    item?.outTime || "00:00:00",
                  ),
        }));

        timeLogBulkChanges(payload);
        return;
      }

      const requestData = {
        companyCode: userData?.companyCode,
        attendanceDate: updatedRow.attendanceDate,
        locationCode: userData?.locationCode,
        fromDate: filterData?.fromDate,
        toDate: filterData?.toDate,
        employeeCode: updatedRow?.employeeCode,
        attendance: [
          {
            ...updatedRow,
            slno: updatedRow?.slNo || 0,
            employeeCode: updatedRow?.employeeCode,
            attendanceValue:
              updatedRow?.inTime === updatedRow?.outTime
                ? 0
                : updatedRow?.isPaidLeave && !updatedRow?.isFullPaidLeave
                  ? 0.5
                  : updatedRow?.isHalfDay
                    ? 0.5
                    : updatedRow?.attendanceValue === 0
                      ? 0
                      : updatedRow?.isPaidLeave
                        ? 0
                        : getAttendanceValue(
                            updatedRow?.inTime || "00:00:00",
                            updatedRow?.outTime || "00:00:00",
                          ),
            inTime: updatedRow?.inTime,
            outTime: updatedRow?.hasOutTime
              ? updatedRow?.outTime
              : updatedRow?.attendanceValue === 0 ||
                  updatedRow?.inTime === "00:00:00" ||
                  updatedRow?.inTime === "00:00"
                ? "00:00:00"
                : null,
            hasOutTime:
              updatedRow?.outTime !== "00:00:00" &&
              updatedRow?.outTime !== "00:00",
            createUser: updatedRow?.createUser || userData?.userName,
            createDate: updatedRow?.createDate || getCurrentFormattedDate(),
            modifyUser: userData?.userName,
            modifyDate: getCurrentFormattedDate(),
            scheduleId: updatedRow?.scheduleId || 0,
            projectCode: updatedRow?.projectCode || "0",
            shiftId: updatedRow?.shiftId || "",
            mode: updatedRow?.mode || "MM",
            waitingTime: updatedRow?.waitingTime || "00:00:00",
            isApproved: updatedRow?.isApproved,
            oT1: updatedRow?.oT1 || 0,
            oT2: updatedRow?.oT2 || 0,
            wtrot: updatedRow?.wtrot || "00:00:00",
            extraCharge: updatedRow?.extraCharge || 0,
            approvedDate: "",
            approvedBy: "",
            normalHours: updatedRow?.normalWorkingHours || 0,
            haveSplit: updatedRow?.haveSplit || false,
            haveShift: updatedRow?.shiftName !== "" ? true : false,
            // isAlternateDayOff: updatedRow?.isAlternateDayOff || false,
            remarks:
              updatedRow?.isAlternateDayOff && updatedRow?.isHalfDay
                ? "Half Rest Day"
                : updatedRow?.remarks === "Rest Day" ||
                    updatedRow?.remarks === "Weekly Off"
                  ? ""
                  : updatedRow?.isHalfDay
                    ? "Half Day"
                    : updatedRow?.remarks || "",
          },
        ],
      };

      // mark id as in-flight (only for numeric ids)
      if (typeof id === "number") {
        updatingIdsRef.current.add(id);
      }

      addAttendanceHandler({ payload: requestData, id: id });

      // remove in-flight mark after a short delay to avoid rapid duplicate calls;
      // this is a simple guard — if you want to remove the id precisely when the request finishes,
      // consider enhancing addAttendanceHandler to return a promise or accept callbacks.
      if (typeof id === "number") {
        setTimeout(() => {
          updatingIdsRef.current.delete(id);
        }, 1500);
      }
    },
    [
      watch,
      userData,
      filterData,
      getAttendanceValue,
      timeLogBulkChanges,
      addAttendanceHandler,
      setValue,
    ],
  );

  const splitAttendanceDelete = (id) => {
    const deletedRow = watch("attendance").find((row) => row.id === id);

    const splitItemData = watch("attendance")?.filter(
      (row) =>
        row?.attendanceDate === deletedRow?.attendanceDate &&
        row?.employeeCode === deletedRow?.employeeCode &&
        row?.id !== id,
    );

    if (deletedRow?.haveSplit) {
      const requestData = {
        companyCode: userData?.companyCode,
        attendanceDate: splitItemData?.[0]?.attendanceDate,
        locationCode: userData?.locationCode,
        fromDate: splitItemData?.[0]?.attendanceDate,
        toDate: splitItemData?.[0]?.attendanceDate,
        employeeCode: splitItemData?.[0]?.employeeCode,
        attendance: splitItemData?.map((s) => ({
          ...s,
          attendanceValue: Number(s.attendanceValue),
          normalHours: s?.normalWorkingHours || 0,
          haveSplit: splitItemData?.length > 1 ? true : false,
          // isAlternateDayOff: s?.isAlternateDayOff || false,
        })),
      };

      addAttendanceHandler({
        payload: requestData,
        id: "",
      });
      return;
    }
  };

  const setTotalWorkingHours = (id, date1, date2) => {
    const diff = date2 - date1;
    const hoursDiff = Math.floor(diff / (1000 * 60 * 60));
    const minutesDiff = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    // const secondsDeiff = Math.floor((Number(diff) % (1000 * 60 * 60)) / (1000 * 60) * (1000*6));
    let totalWorkingHours = `${hoursDiff}:${minutesDiff}:00`;

    // setValue(`attendance[${id}].totalWorkingHours`, totalWorkingHours);
    setValue(
      `attendance[${id}].attendanceValue`,
      totalWorkingHours === "0:0:00" || totalWorkingHours === "0:0" ? 0 : 1,
    );
  };

  const defaultStates = {};

  const calculateTotalWorkingHours = useCallback(
    async (id) => {
      const inTime = watch(`attendance[${id}].inTime`);
      const outTime = watch(`attendance[${id}].outTime`);

      if (
        !inTime ||
        inTime === "00:00:00" ||
        !outTime ||
        outTime === "00:00:00"
      )
        return;

      const startTime = moment(inTime, "HH:mm");
      const endTime = moment(outTime, "HH:mm");
      const totalWorkingHours = moment
        .utc(endTime.diff(startTime))
        .format("HH:mm");

      setValue(`attendance[${id}].totalWorkingHours`, totalWorkingHours);
      setValue(
        `attendance[${id}].attendanceValue`,
        totalWorkingHours === "0:0:00" || totalWorkingHours === "0:0" ? 0 : 1,
      );
      handleUpdateAttendance(id);
    },
    [watch("attendance")],
  );

  // const [leaveState, dispatch] = useReducer(attendanceHandler, defaultStates);

  const refetchTimeLogHandler = useCallback(() => {
    if (filterData?.fromDate !== filterData?.toDate) {
      if (filterData?.employeeCode === 0 || !filterData.employeeCode) {
        Swal.fire({
          title: "Warning!",
          text: "Please select employee for flexible search",
          type: "warning",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Ok",
          confirmButtonClass: "btn btn-primary",
          okButtonClass: "",
          buttonsStyling: 1,
        });
      } else {
        // setValue("attendance", []);
        refetchTimeLog();
      }
    } else {
      // setValue("attendance", []);
      refetchTimeLog();
    }
    setSelectedRow([]);
    setValue("attendanceFilterData", filterData);
  }, [filterData, refetchTimeLog]);

  const timeLogBulkSubmit = useCallback(async (data) => {
    const timelog = data?.manualEntryData;

    const requestData = {
      companyCode: userData?.companyCode,
      fromDate: timelog?.fromDate,
      toDate: timelog?.toDate,
      locationCode: userData?.locationCode,
      bulkAttendance: [
        {
          employeeCodes:
            timelog?.selectedEmployees.length > 0
              ? timelog?.selectedEmployees?.map((e) => e.value)
              : [timelog?.employeeCode],

          attendanceValue:
            timelog?.inTime === timelog?.outTime
              ? 0
              : timelog?.isHalfDay
                ? 0.5
                : timelog?.attendanceValue === 0
                  ? 0
                  : timelog?.isPaidLeave && !timelog?.isFullPaidLeave
                    ? 0.5
                    : getAttendanceValue(timelog?.inTime, timelog?.outTime),
          inTime: timelog?.inTime,
          outTime: timelog?.outTime,
          createUser: timelog?.createUser || userData?.userName,
          createDate: timelog?.createDate || getCurrentFormattedDate(),
          modifyUser: userData?.userName,
          modifyDate: getCurrentFormattedDate(),
          scheduleId: 0,
          projectCode: timelog?.projectId || "",
          shiftId: timelog?.shiftId || "",
          remarks: timelog?.isHalfDay ? "Half Day" : "",
          mode: "MM",
          waitingTime: "00:00:00",
          isApproved: false,
          oT1: 0,
          oT2: 0,
          extraCharge: 0,
          wtrot: "00:00:00",
          wtnhEdit: false,
          supplierCompany: "",
          service: "",
          isReviewed: false,
          reviewDate: "",
          reviewBy: "",
          approvedDate: "",
          approvedBy: "",
          normalHours: timelog?.normalWorkingHours || 0,
          haveSplit: timelog?.haveSplit || false,
          haveShift: timelog?.shiftName !== "" ? true : false,
          totalWorkingHours: 0,
          hasOutTime:
            timelog?.outTime && timelog?.outTime !== "00:00:00" ? true : false,
          // isAlternateDayOff: timelog?.isAlternateDayOff || false,
        },
      ],
    };

    addBulkAttendanceHandler(requestData);
    // const isValid = await triggerBulkChange();
    // if (isValid) {
    //
    // } else {
    //   toast.error("Validation errors occurred.");
    //   console.error("Validation errors:", isValid);
    // }

    //
  });

  const handleTimesheetDownload = () => {
    handleTimesheetPrint();
  };

  const handleTimesheetPrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `${employeeName} Timesheet ${filterData?.fromDate}-${filterData?.toDate}`,
    // pageStyle: TimelogPrintProperties
    // onBeforePrint :
  });

  const arrangeAttendanceData = useCallback(() => {
    if (fetchedTimeLogData && fetchedTimeLogData?.attendanceDetails?.length) {
      const sortedData = fetchedTimeLogData?.attendanceDetails
        // .sort((a, b) => (a.attendanceDate > b.attendanceDate ? 1 : -1))
        .map((d, index) => ({
          id: index,
          ...d,
          // attendanceValue: Number(d.attendanceValue), // latest changes
        }));

      setEmployeeName(sortedData?.[0]?.employeeName);
      setValue("attendance", sortedData);
    }
  }, [fetchedTimeLogData]);

  const editHandler = (id) => {
    const attendance = watch("attendance");
    const index = attendance.findIndex((a) => a.id === id);

    if (id !== -1) {
      setValue(`attendance[${id}].isApproved`, false);
      setValue(`attendance[${id}].isReviewed`, false);
      fetchedTimeLogData.attendanceDetails[index].isApproved = false;
      fetchedTimeLogData.attendanceDetails[index].isReviewed = false;
      handleUpdateAttendance(id);
    } else {
      console.warn(`No attendance entry found for id: ${id}`);
    }
  };

  const waitingTimeHandler = (type = "NOT") => {
    const otActualHr =
      Number(fetchedTimeLogData?.workingSummary?.[0]?.[0]?.oT1).toFixed(2) || 0;
    if (type === "NOT") {
      const payload = {
        employeeCode: filterData?.employeeCode,
        companyCode: userData?.companyCode,
        startDate: filterData?.fromDate,
        endDate: filterData?.toDate,
        otActualHr: Number(otActualHr),
        otLimit: 72,
      };

      adjustWaitingTime(payload, {
        onSuccess: (data) => {
          if (data?.response === true) {
            toast.success(
              data?.message || "Waiting Time Updated Successfully!",
            );
            refetchTimeLog();
          } else {
            toast.warning(data?.message || "Something went wrong.");
          }
        },
        onerror: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    } else if (type === "WTROT") {
      const payload = {
        employeeCode: filterData?.employeeCode,
        companyCode: userData?.companyCode,
        startDate: filterData?.fromDate,
        endDate: filterData?.toDate,
      };
      adjustWTROTWaitingTime(payload, {
        onSuccess: (data) => {
          if (data?.response === true) {
            toast.success(
              data?.message || "WTROT Waiting Time Updated Successfully!",
            );
            refetchTimeLog();
          } else {
            toast.warning(data?.message || "Something went wrong.");
          }
        },
        onerror: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    }
  };

  useEffect(() => {
    arrangeAttendanceData();
  }, [fetchedTimeLogData]);

  useEffect(() => {
    if (isMount) refetchTimeLogHandler();

    setIsMount(true);
  }, [filterData]);

  const bulkEntryHandler = () => {
    resetBulkEnteryData();
    setShow(true);
  };
  const attendance = watch("attendance");

  const hasApproved = useMemo(() => {
    return selectedRow.some((selected) =>
      watch("attendance").some(
        (record) => record.id === selected && record.isApproved === true,
      ),
    );
  }, [selectedRow, watch("attendance")]);

  const hasReviewed = useMemo(() => {
    return selectedRow.some((selected) =>
      watch("attendance").some(
        (record) => record.id === selected && record.isReviewed === true,
      ),
    );
  }, [selectedRow, watch("attendance")]);

  if (isLoading || isFetching) return <LoadingSpinner />;

  return (
    <div className="page-wrapper">
      <div className="content mb-3 timelog">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Attendance Management</h4>
              <h6>Manage attendance time log</h6>
            </div>
          </div>
          <TimelogFilterSection
            companyCode={userData?.companyCode}
            locationCode={userData?.locationCode}
            filterData={filterData}
            setFilterData={setFilterData}
            refetchTimeLogHandler={refetchTimeLogHandler}
            setSearchParams={setSearchParams}
          />

          <div className="page-btn">
            <Can
              accessModule={accessCodes.time_attendance}
              accessCode={accessCodes.time_tracking}
              accessType="haveAdd"
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="add-new-tooltip">Bulk Entry</Tooltip>}
              >
                <Link
                  to="#"
                  className="btn btn-added"
                  onClick={bulkEntryHandler}
                  style={{ height: "30px" }}
                >
                  <PlusCircle className="me-2" size={15} />
                  Bulk Entry
                </Link>
              </OverlayTrigger>
            </Can>
          </div>

          <div className="page-btn">
            <Can
              accessModule={accessCodes.time_attendance}
              accessCode={accessCodes.time_tracking}
              accessType="haveAdd"
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="add-new-tooltip">Bulk Entry</Tooltip>}
              >
                <Link
                  to={`${all_routes.attendance}${all_routes.import_time_log}`}
                  className="btn btn-added"
                  onClick={bulkEntryHandler}
                  style={{ height: "30px" }}
                >
                  <Download className="me-2" size={15} />
                  Import Timelog
                </Link>
              </OverlayTrigger>
            </Can>
          </div>

          {/* <div className="page-btn">
            <WeeklyOff
              onSubmit={() => {}}
              selectedRow={selectedRow}
              attendance={watch("attendance")}
              hasApproved={hasApproved}
              hasReviewed={hasReviewed}
            />
          </div> */}
          {filterData?.employeeCode > 0 && (
            <div className="page-btn">
              <Can
                accessModule={accessCodes.time_attendance}
                accessCode={accessCodes.time_tracking}
                accessType="haveEdit"
              >
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="add-new-tooltip">
                      Download TImelog Sheet
                    </Tooltip>
                  }
                >
                  <Link
                    to="#"
                    // to={`${all_routes.attendance}${all_routes.download_timelog}`}
                    className="btn btn-added"
                    onClick={handleTimesheetDownload}
                    style={{ height: "30px" }}
                  >
                    <Download size={15} />
                  </Link>
                </OverlayTrigger>
              </Can>
            </div>
          )}
        </div>

        {filterData?.employeeCode > 0 && (
          <h4 className="timelog-employee"> Employee : {employeeName}</h4>
        )}

        <div className="total-employees body d-flex align-items-center gap-5">
          <h6>
            <Users className="me-2" />
            Total Employees
            <span className="primary">
              {fetchedTimeLogData?.attendanceCounts?.[0]?.totalEmployees}
            </span>
          </h6>
          <h6>
            <Users className="me-2" />
            Total Present
            <span className="success">
              {fetchedTimeLogData?.attendanceCounts?.[0]?.presentCount || 0}
            </span>
          </h6>
          <h6>
            <Users className="me-2" />
            Total Absent
            <span className="danger">
              {fetchedTimeLogData?.attendanceCounts?.[0]?.absentCount || 0}
            </span>
          </h6>
        </div>
        <FormProvider {...methods}>
          <TimelogTable
            isLoading={isFetching || isLoading || timelogUpdating}
            isSingleEmployee={filterData?.employeeCode > 0}
            attendanceData={attendance}
            handleUpdateAttendance={handleUpdateAttendance}
            addAttendanceHandler={addAttendanceHandler}
            setTotalWorkingHours={setTotalWorkingHours}
            setSelectedEmployee={setSelectedRow}
            selectedEmployee={selectedRow}
            shiftOptions={shiftOptions}
            shifts={shifts}
            projectsOptions={projectsOptions}
            editHandler={editHandler}
            splitAttendanceDelete={splitAttendanceDelete}
            permissions={userData?.permissions || []}
            description={fetchedTimeLogData?.headerDescription || []}
            markWeeklyOffHandler={markWeeklyOffHandler}
          />
        </FormProvider>

        {filterData?.employeeCode > 0 && (
          <div className="card border-0 mb-3">
            <div className="card-header">
              <div className="card-title">Summary</div>
            </div>
            <div className="card-body ">
              <FormProvider {...bulkEntryMethods}>
                <Summary
                  attendance={watch("attendance")}
                  selectedRow={selectedRow}
                  fetchedTimeLogData={fetchedTimeLogData}
                  onApprove={timeLogBulkChanges}
                  waitingTimeHandler={waitingTimeHandler}
                />
              </FormProvider>
            </div>
          </div>
        )}
        {selectedRow?.length > 0 && (
          <div className="card mb-3 border-0">
            <div className="card-header">
              <div className="card-title">Bulk Changes</div>
            </div>
            <div className="card-body ">
              {hasApproved ? (
                <div className="text-center">
                  <h6 className="text-danger">
                    Approved attendance can&apos;t be changed
                  </h6>
                </div>
              ) : (
                <FormProvider {...bulkEntryMethods}>
                  <BulkChange
                    attendance={watch("attendance")}
                    selectedRow={selectedRow}
                    shifts={shifts}
                    shiftOptions={shiftOptions}
                    projectsOptions={projectsOptions}
                    onSubmit={timeLogBulkChanges}
                    resetData={resetData}
                    setResetData={setResetData}
                    hasApproved={hasApproved}
                    hasReviewed={hasReviewed}
                  />
                </FormProvider>
              )}
              {/* {Array.isArray(fetchedTimeLogData?.attendanceDetails) &&
              !fetchedTimeLogData.attendanceDetails.every(
                (obj) => obj.isApproved
              ) ? (
                <FormProvider {...bulkEntryMethods}>
                  <BulkChange
                    attendance={watch("attendance")}
                    selectedRow={selectedRow}
                    shifts={shifts}
                    shiftOptions={shiftOptions}
                    projectsOptions={projectsOptions}
                    onSubmit={timeLogBulkChanges}
                  />
                </FormProvider>
              ) : (
                <div className="text-center">
                  <h6 className="text-danger">
                    Approved attendance can&apos;t be changed
                  </h6>
                </div>
              )} */}
            </div>
          </div>
        )}
      </div>
      <FormProvider {...methods}>
        <TimeLogModal
          mode="I"
          onSubmit={timeLogBulkSubmit}
          show={show}
          handleClose={handleClose}
          shiftOptions={shiftOptions}
        />
      </FormProvider>
      <div style={{ display: "none" }}>
        {fetchedTimeLogData && Object.keys(fetchedTimeLogData)?.length > 0 && (
          <TimelogSheet
            ref={componentRef}
            timelogData={fetchedTimeLogData ?? {}}
            companyDetails={{
              companyName: userData?.companyName,
              companyLogo: userData?.companyLogo,
            }}
            // uniqueId={filterData?.employeeCode || 0}
            employeeName={employeeName}
            // show={showTimeSheet}
            // handleClose={handleClose}
          />
        )}
      </div>
    </div>
  );
};
export default React.memo(TimeLog);
